<template>
    <div class="ticket-stack__container-anchor">
        <div class="ticket-stack__container">
            <transition-group
                name="animation__scale-up-ver-bottom-"
                tag="div"
                class="ticket-stack__widgets-container"
            >
                <TicketsWidget
                    v-for="(ticket, index) in shownTickets"
                    :key="ticket.id"
                    v-bind="ticket"
                    :order="-index"
                    :isRecreatingTicket="isRecreatingTicket"
                    @close="handleWidgetClose(ticket.id)"
                    @click="handleWidgetToggle(ticket.id)"
                    @recreate="onRecreateTicket(ticket)"
                    @detailsClick="onDetailsClick(ticket.id)"
                />
            </transition-group>
            <FoldedTickets
                v-if="compressedTickets.length"
                :tickets="compressedTickets"
                :isExpanded="isCompressedStackExpanded"
                @close="handleWidgetClose"
                @unfold="unfoldTicket"
                @toggleFolded="handleCompressedStackToggling"
            />
            <AllTicketsStack
                :isExpanded="isAllTicketsStackOpen"
                :tickets="allTickets"
                :isAnyTicketOpened="shownTickets.length !== 0"
                @ticketClick="handleAllStackTicketClick"
                @toggleAll="handleAllTicketsStackToggling"
                @closeAll="onCloseAll"
            />
        </div>
    </div>
</template>

<script>
    import {
        keyBy as _keyBy,
        differenceBy as _differenceBy,
    } from '@lodash';
    import { TicketStatus, EventStatus } from '@core/models/tickets/enums';
    import { Keys, getKey } from '@app/utils/keyListener';
    import TicketsWidget from './components/TicketsWidget';
    import FoldedTickets from './components/FoldedTickets';
    import AllTicketsStack from './components/AllTicketsStack';
    import styleVars from './styleVars';

    function windowWidth() {
        const width = window.innerWidth
            || document.documentElement.clientWidth
            || document.body.clientWidth;
        return width;
    }

    function numWidgetsThatFit() {
        const {
            outerMargin,
            innerMargin,
            itemWidth,
            toggleIconWidth,
        } = styleVars;
        const availableWidth = windowWidth() - 3 * outerMargin - innerMargin - toggleIconWidth;
        const num = Math.floor(availableWidth / (itemWidth + innerMargin)) - 1;
        return Math.max(num, 0);
    }

    export default {
        props: {
            tickets: {
                type: Array,
                default() {
                    return [];
                },
            },
            highlightedTickets: {
                type: Object,
                required: true,
            },
            isRecreatingTicket: {
                type: Boolean,
                required: true,
            }
        },
        data() {
            return {
                isCompressedStackExpanded: false,
                isAllTicketsStackOpen: false,
                spreadTicketIds: [],
                compressedTicketIds: [],
                expandedTicketIds: [],
            };
        },
        mounted() {
            this.previousHighlightedTickets = { ...this.highlightedTickets };
            this.checkItems();
            this.resizeListener = window.addEventListener('resize', this.checkItems);
            this.keyboardListener = window.addEventListener('keyup', this.onKeyup);
        },
        watch: {
            tickets(newTickets, oldTickets) {
                const newlyReceivedTickets = this.extractNewlyReceivedTickets(newTickets, oldTickets);
                const newlyCreatedTickets = this.extractNewlyCreatedTickets(newlyReceivedTickets);
                this.removeOldTickets(newTickets, oldTickets);

                newlyCreatedTickets.forEach((ticket) => {
                    this.expandedTicketIds.push(ticket.id);
                    this.spreadTicketIds.push(ticket.id);
                });
                this.checkItems(true);
                if (newTickets.length === 0) {
                    this.isAllTicketsStackOpen = false;
                }
            },
            highlightedTickets: {
                handler(value) {
                    const newHighlightedTicketIds = [];
                    /*
                        searches tickets that have been highlighted since the last change.
                        This is a 'hack'. Oldvalue cannot be used beacuse the objects are
                        same by reference
                    */
                    Object.entries(value).forEach(([key, val]) => {
                        if (val && !this.previousHighlightedTickets[val]) {
                            newHighlightedTicketIds.push(key);
                        }
                    });
                    this.highlightTickets(newHighlightedTicketIds);
                },
                deep: true,
            },
            'compressedTicketIds.length': function (newValue) {
                if (newValue === 0) {
                    this.isCompressedStackExpanded = false;
                }
            }
        },
        computed: {
            shownTickets() {
                return this.spreadTicketIds.map((id) => this.allTicketsMap[id]);
            },
            compressedTickets() {
                return this.compressedTicketIds.map((id) => this.allTicketsMap[id]);
            },
            allTicketsMap() {
                return _keyBy(this.allTickets, 'id');
            },
            allTickets() {
                const self = this;
                return this.tickets.map(({
                    id,
                    type,
                    system,
                    status,
                    coefficient,
                    events,
                    liveEvents,
                    hasOngoingEvents,
                    dateReceived,
                    payment: {
                        total,
                    },
                    win: {
                        estimated,
                        payoff,
                        minPotential,
                    },
                    canRecreate,
                }) => ({
                    id,
                    type,
                    status,
                    coefficient,
                    system,
                    potentialWin: estimated,
                    minimumPotentialWin: minPotential,
                    payoff,
                    events,
                    isWon: status === TicketStatus.win,
                    hasOngoingEvents,
                    liveEvents,
                    dateReceived,
                    stake: total,
                    numEventsLeft: status === TicketStatus.active
                        ? events.filter((event) => event.status === EventStatus.active).length
                        : 0,
                    // ui props
                    get isExpanded() {
                        return self.expandedTicketIds.indexOf(id) > -1;
                    },
                    /* eslint-disable vue/no-side-effects-in-computed-properties */
                    set isExpanded(value) {
                        const openIndex = self.expandedTicketIds.indexOf(id);
                        if (value && openIndex === -1) {
                            self.expandedTicketIds.push(id);
                        } else if (!value && openIndex !== -1) {
                            self.expandedTicketIds.splice(openIndex, 1);
                        }
                    },
                    get isSpread() {
                        return self.spreadTicketIds.indexOf(id) > -1;
                    },
                    set isSpread(value) {
                        const outerIndex = self.spreadTicketIds.indexOf(id);
                        if (value && outerIndex === -1) {
                            self.spreadTicketIds.push(id);
                        } else if (!value && outerIndex !== -1) {
                            self.spreadTicketIds.splice(outerIndex, 1);
                        }
                    },
                    get isCompressed() {
                        return self.compressedTicketIds.indexOf(id) > -1;
                    },
                    set isCompressed(value) {
                        const foldedIndex = self.compressedTicketIds.indexOf(id);
                        if (value && foldedIndex === -1) {
                            self.compressedTicketIds.push(id);
                        } else if (!value && foldedIndex !== -1) {
                            self.compressedTicketIds.splice(foldedIndex, 1);
                        }
                    },
                    /* eslint-enable vue/no-side-effects-in-computed-properties */
                    isHighlighted: this.highlightedTickets[id],
                    canRecreate,
                }));
            },
        },
        methods: {
            handleWidgetClose(id) {
                const ticket = this.allTicketsMap[id];
                if (ticket.isCompressed) {
                    ticket.isCompressed = false;
                } else if (ticket.isSpread) {
                    ticket.isSpread = false;
                }
                ticket.isExpanded = false;
                if (ticket.isHighlighted) {
                    this.$emit('ticketClick', id);
                }
                this.checkItems();
            },
            handleWidgetToggle(id) {
                const ticket = this.allTicketsMap[id];
                if (ticket.isHighlighted) {
                    this.$emit('ticketClick', id);
                }
                ticket.isExpanded = !ticket.isExpanded;
            },
            handleAllStackTicketClick(id) {
                const ticket = this.allTicketsMap[id];
                if (!ticket.isExpanded) {
                    this.$emit('ticketOpen', id);
                }
                if (ticket.isHighlighted) {
                    this.$emit('ticketClick', id);
                }

                if (ticket.isCompressed) {
                    ticket.isCompressed = false;
                    ticket.isExpanded = true;
                } else if (ticket.isSpread) {
                    if (ticket.isExpanded) {
                        ticket.isExpanded = false;
                        ticket.isSpread = false;
                    } else {
                        ticket.isExpanded = true;
                    }
                } else {
                    ticket.isSpread = true;
                    ticket.isExpanded = true;
                }
                this.checkItems(true);
            },
            unfoldTicket(id) {
                const ticket = this.allTicketsMap[id];
                ticket.isCompressed = false;
                ticket.isExpanded = true;
                this.spreadTicketIds.push(id);
                this.checkItems(true);
            },
            // manages tickets collapsed/expanded state if all tickets don't fit on the screen
            checkItems(isFifo) {
                const num = numWidgetsThatFit();
                if (this.spreadTicketIds.length > num) {
                    while (this.spreadTicketIds.length > num) {
                        if (isFifo) {
                            const id = this.spreadTicketIds.shift();
                            this.compressedTicketIds.unshift(id);
                        } else {
                            const id = this.spreadTicketIds.pop();
                            this.compressedTicketIds.push(id);
                        }
                    }
                } else if (num > this.spreadTicketIds.length) {
                    while ((num > this.spreadTicketIds.length) && this.compressedTicketIds.length > 0) {
                        const id = this.compressedTicketIds.shift();
                        this.spreadTicketIds.unshift(id);
                    }
                }
                this.closeLeftmostTicket();
            },
            handleCompressedStackToggling(value) {
                if (value !== undefined) {
                    this.isCompressedStackExpanded = value;
                } else {
                    this.isCompressedStackExpanded = !this.isCompressedStackExpanded;
                }
                this.closeLeftmostTicket();
            },
            handleAllTicketsStackToggling() {
                this.isAllTicketsStackOpen = !this.isAllTicketsStackOpen;
            },
            closeLeftmostTicket() {
                if (this.isCompressedStackExpanded && this.compressedTicketIds.length && this.spreadTicketIds.length) {
                    const leftMostItem = this.spreadTicketIds[0];
                    this.allTicketsMap[leftMostItem].isExpanded = false;
                }
            },
            highlightTickets(ticketIds) {
                ticketIds.forEach((id) => {
                    const ticket = this.allTicketsMap[id];
                    if (ticket) {
                        if (!ticket.isSpread) {
                            ticket.isSpread = true;
                            ticket.isCompressed = false;
                        }
                    }
                });
                this.checkItems();
            },
            onKeyup(event) {
                const key = getKey(event);
                if (key === Keys.ESC) {
                    this.isAllTicketsStackOpen = false;
                }
            },
            onRecreateTicket(ticket) {
                this.isAllTicketsStackOpen = false;
                this.$emit('recreate', ticket);
            },
            onCloseAll() {
                this.allTickets.forEach((ticket) => {
                    this.handleWidgetClose(ticket.id);
                });
            },
            removeOldTickets(newTickets, oldTickets) {
                const newTicketsMap = _keyBy(newTickets, 'id');
                oldTickets.forEach((ticket) => {
                    if (!newTicketsMap[ticket.id]) {
                        const compressedIndex = this.compressedTicketIds.indexOf(ticket.id);
                        const expandedIndex = this.expandedTicketIds.indexOf(ticket.id);
                        const spreadIndex = this.spreadTicketIds.indexOf(ticket.id);

                        if (compressedIndex !== -1) {
                            this.compressedTicketIds.splice(compressedIndex, 1);
                        }
                        if (expandedIndex !== -1) {
                            this.expandedTicketIds.splice(expandedIndex, 1);
                        }
                        if (spreadIndex !== -1) {
                            this.spreadTicketIds.splice(spreadIndex, 1);
                        }
                    }
                });
            },
            extractNewlyReceivedTickets(newTickets, oldTickets) {
                return _differenceBy(newTickets, oldTickets, 'id');
            },
            extractNewlyCreatedTickets(tickets) {
                return tickets.filter((ticket) =>
                    Math.floor(ticket.dateReceived.getTime() / 1000)
                    === Math.floor(ticket.dateLastModified.getTime() / 1000));
            },
            onDetailsClick(ticket) {
                this.$emit('detailsClick', ticket);
            },
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.resizeListener);
            window.removeEventListener('keyup', this.keyboardListener);
        },
        components: {
            TicketsWidget,
            FoldedTickets,
            AllTicketsStack,
        },
    };
</script>


// WEBPACK FOOTER //
// src/app/modules/ticketsWidget/TicketsUIContainer.vue